import { Node, Edge } from 'reactflow';
import { ExecutionFlowValidation, PipelineStep, InputSource } from '../types/pipeline';

/**
 * Calculate execution order by walking the execution flow chain starting from Input node
 * @param nodes - All nodes in the pipeline
 * @param edges - All edges in the pipeline  
 * @returns Map of nodeId to execution order (0, 1, 2...)
 */
export function calculateExecutionOrder(
  nodes: Node[],
  edges: Edge[]
): Map<string, number> {
  const orderMap = new Map<string, number>();

  // Start from Input node (always order 0)
  let currentNodeId: string | null = 'input';
  let order = 0;

  // Walk the execution chain
  while (currentNodeId) {
    orderMap.set(currentNodeId, order);

    // Find next node in execution chain
    const currentId: string = currentNodeId; // Capture for closure
    const nextEdge = edges.find(
      (e) => e.source === currentId && e.sourceHandle === 'next_node'
    );

    currentNodeId = nextEdge?.target ?? null;
    order++;
  }

  return orderMap;
}

/**
 * Validate the execution flow chain
 * @param nodes - All nodes in the pipeline
 * @param edges - All edges in the pipeline
 * @returns Validation result with errors
 */
export function validateExecutionChain(
  nodes: Node[],
  edges: Edge[]
): ExecutionFlowValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Calculate current order map
  const orderMap = calculateExecutionOrder(nodes, edges);

  // Get all execution edges
  const executionEdges = edges.filter((e) => e.sourceHandle === 'next_node');
  const dataEdges = edges.filter((e) => e.sourceHandle !== 'next_node');

  // Rule 1: All nodes (except Input) must be in execution chain
  const nonInputNodes = nodes.filter((n) => n.id !== 'input');
  const orphans = nonInputNodes.filter((n) => !orderMap.has(n.id));

  if (orphans.length > 0) {
    const orphanNames = orphans.map((n) => n.data.name || n.id).join(', ');
    errors.push(
      `${orphans.length} node(s) not in execution chain: ${orphanNames}`
    );
  }

  // Rule 2: Exactly one end node (node with no outgoing execution edge)
  const endNodes = nonInputNodes.filter((n) => {
    const hasNextEdge = executionEdges.some((e) => e.source === n.id);
    return !hasNextEdge && orderMap.has(n.id); // In chain but no next
  });

  if (orderMap.size > 1) {
    // Only check if there's more than just Input
    if (endNodes.length === 0) {
      errors.push('No end node found - execution chain may form a cycle');
    } else if (endNodes.length > 1) {
      const endNodeNames = endNodes.map((n) => n.data.name || n.id).join(', ');
      errors.push(
        `Multiple end nodes found (must be exactly 1): ${endNodeNames}`
      );
    }
  }

  // Rule 3: No cycles in execution flow
  const visited = new Set<string>();
  const inPath = new Set<string>();

  function hasCycle(nodeId: string): boolean {
    if (inPath.has(nodeId)) {
      return true; // Cycle detected
    }
    if (visited.has(nodeId)) {
      return false; // Already checked
    }

    visited.add(nodeId);
    inPath.add(nodeId);

    const nextEdge = executionEdges.find((e) => e.source === nodeId);
    if (nextEdge && hasCycle(nextEdge.target)) {
      return true;
    }

    inPath.delete(nodeId);
    return false;
  }

  if (hasCycle('input')) {
    errors.push('Execution chain contains a cycle');
  }

  // Rule 4: Data dependencies must respect execution order
  for (const edge of dataEdges) {
    const sourceOrder = orderMap.get(edge.source);
    const targetOrder = orderMap.get(edge.target);

    if (
      sourceOrder !== undefined &&
      targetOrder !== undefined &&
      sourceOrder >= targetOrder
    ) {
      const sourceName =
        nodes.find((n) => n.id === edge.source)?.data.name || edge.source;
      const targetName =
        nodes.find((n) => n.id === edge.target)?.data.name || edge.target;

      errors.push(
        `Invalid dependency: "${targetName}" (order ${targetOrder}) depends on ` +
          `"${sourceName}" (order ${sourceOrder}), but "${sourceName}" executes ` +
          `after or at the same time`
      );
    }
  }

  // Warning: Input node should have at least one next node
  if (orderMap.size === 1) {
    warnings.push('Pipeline only contains Input node - add processors to build your pipeline');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Reconstruct execution flow edges from pipeline steps with order
 * Used when loading a pipeline from backend
 * @param steps - Pipeline steps from backend (have order field)
 * @returns Array of execution flow edges
 */
export function reconstructExecutionEdges(steps: PipelineStep[]): Edge[] {
  const sorted = [...steps].sort((a, b) => a.order - b.order);
  const edges: Edge[] = [];

  // SWRM-128: Input processor is now part of steps, so don't add fake input
  // Just use the sorted steps as-is
  for (let i = 0; i < sorted.length - 1; i++) {
    const currentStep = sorted[i];
    const nextStep = sorted[i + 1];

    edges.push({
      id: `exec-${currentStep.step_id}-to-${nextStep.step_id}`,
      source: currentStep.step_id,
      sourceHandle: 'next_node',
      target: nextStep.step_id,
      targetHandle: 'previous_node',
      type: 'execution',
      animated: false,
      style: {
        stroke: '#f5f5f5',
        strokeWidth: 4,
      },
    });
  }

  return edges;
}

/**
 * Build sub-agent chain steps for an orchestrator (SWRM-121)
 * @param orchestratorId - The orchestrator node ID
 * @param subAgentName - Name of the sub-agent
 * @param nodes - All nodes in the pipeline
 * @param edges - All edges in the pipeline
 * @returns Object with entry_node_id and steps array
 */
function buildSubAgentChainSteps(
  orchestratorId: string,
  subAgentName: string,
  nodes: Node[],
  edges: Edge[]
): { entry_node_id: string | null; steps: any[] } {
  // Find all nodes in this sub-agent
  const subAgentNodes = nodes.filter(
    (n) => n.data.orchestratorNodeId === orchestratorId && n.data.subAgentName === subAgentName
  );
  
  if (subAgentNodes.length === 0) return { entry_node_id: null, steps: [] };
  
  // Find the first node (connected directly to orchestrator's sub_agent_output)
  // Need to find the edge that connects to THIS sub-agent (not just any sub_agent_output edge)
  const orchestratorEdge = edges.find((e) => {
    if (e.source !== orchestratorId || e.sourceHandle !== 'sub_agent_output') {
      return false;
    }
    // Check if the target node belongs to this sub-agent
    const targetNode = nodes.find((n) => n.id === e.target);
    return targetNode?.data.subAgentName === subAgentName;
  });
  
  if (!orchestratorEdge) return { entry_node_id: null, steps: [] };
  
  // Build chain by following next_node connections
  const chain: Node[] = [];
  let currentNodeId: string | null = orchestratorEdge.target;
  const visited = new Set<string>();
  
  /* eslint-disable no-loop-func */
  while (currentNodeId && !visited.has(currentNodeId)) {
    visited.add(currentNodeId);
    
    const node = nodes.find((n) => n.id === currentNodeId);
    if (!node || node.data.orchestratorNodeId !== orchestratorId || node.data.subAgentName !== subAgentName) {
      break; // Left the sub-agent chain
    }
    
    chain.push(node);
    
    // Find next node
    const nextEdge = edges.find(
      (e) => e.source === currentNodeId && e.sourceHandle === 'next_node'
    );
    
    if (!nextEdge) break;
    currentNodeId = nextEdge.target;
  }
  /* eslint-enable no-loop-func */
  
  // Build steps with .# placeholder
  const orchestratorStepId = nodes.find((n) => n.id === orchestratorId)?.data.step_id || orchestratorId;
  const dataEdges = edges.filter((e) => e.sourceHandle !== 'next_node' && e.sourceHandle !== 'sub_agent_output');
  
  const steps = chain.map((node, index) => {
    // Build input_artifact_keys from data edges
    // Preserve type information from node.data.input_artifact_keys if available
    const inputKeys: Record<string, any> = {};
    
    const incomingDataEdges = dataEdges.filter((e) => e.target === node.id);
    for (const edge of incomingDataEdges) {
      if (edge.targetHandle && edge.sourceHandle) {
        const inputKey = edge.targetHandle.replace(/^input-/, '');
        
        // Get existing input config to preserve type information
        const existingInput = node.data.input_artifact_keys?.[inputKey];
        
        // Check if this is an LLM Output connection
        if (edge.sourceHandle === 'llm_output') {
          inputKeys[inputKey] = {
            source: 'orchestrator_llm' as InputSource,
            from_step: edge.source, // Orchestrator ID
            key: inputKey,
            ...(existingInput?.type && { type: existingInput.type }), // Preserve type if available
          };
        } else {
          const outputKey = edge.sourceHandle.replace(/^output-/, '');
          inputKeys[inputKey] = {
            source: 'artifact' as InputSource,
            from_step: edge.source,
            key: outputKey,
            ...(existingInput?.type && { type: existingInput.type }), // Preserve type if available
          };
        }
      }
    }
    
    // Generate step_id with .# placeholder
    const processorName = node.data.processor_name;
    const timestamp = node.data.step_id.split('_').pop() || Date.now();
    
    // CRITICAL: Remove any existing .# suffix from timestamp before using it
    // This prevents double placeholders (.#.#) when building nested sub-agent chains
    const cleanTimestamp = String(timestamp).replace(/\.#$/, '');
    
    const stepId = `${orchestratorStepId}.${subAgentName}.${processorName}_${cleanTimestamp}.#`;
    
    // Determine next_step (if not last in chain)
    const nextNode = chain[index + 1];
    const nextStepId = nextNode 
      ? (() => {
          const nextTimestamp = nextNode.data.step_id.split('_').pop() || '';
          const cleanNextTimestamp = String(nextTimestamp).replace(/\.#$/, '');
          return `${orchestratorStepId}.${subAgentName}.${nextNode.data.processor_name}_${cleanNextTimestamp}.#`;
        })()
      : undefined;
    
    // Build config - if this is a nested orchestrator, recursively build its sub_agents
    let stepConfig = node.data.config || {};
    if (node.type === 'orchestrator' || node.data.processor_name === 'orchestrator') {
      const existingSubAgents = (stepConfig.sub_agents || []) as any[];
      
      // Recursively build sub_agents array with chain steps and entry_node_id
      const updatedSubAgents = existingSubAgents.map((nestedSubAgent: any) => {
        const nestedChainData = buildSubAgentChainSteps(node.id, nestedSubAgent.name, nodes, edges);
        
        return {
          name: nestedSubAgent.name,
          display_name: nestedSubAgent.display_name,
          description: nestedSubAgent.description,
          entry_node_id: nestedChainData.entry_node_id,
          steps: nestedChainData.steps,
        };
      });
      
      stepConfig = {
        ...stepConfig,
        sub_agents: updatedSubAgents,
      };
    }
    
    // Include custom_inputs in config (for processors with dynamic inputs)
    if (node.data.custom_inputs && node.data.custom_inputs.length > 0) {
      stepConfig = {
        ...stepConfig,
        custom_inputs: node.data.custom_inputs,
      };
    }

    // Include dynamic_outputs in config (from <<variable>> syntax in prompts)
    if (node.data.dynamic_outputs && node.data.dynamic_outputs.length > 0) {
      stepConfig = {
        ...stepConfig,
        dynamic_outputs: node.data.dynamic_outputs,
      };
    }
    
    return {
      step_id: stepId,
      processor_name: node.data.processor_name,
      name: node.data.name,
      input_artifact_keys: inputKeys,
      config: stepConfig,
      on_fail: node.data.on_fail || 'PAUSE',
      ui_position: { x: Math.round(node.position.x), y: Math.round(node.position.y) },
      ...(node.data.prompt_anchor && { prompt_anchor: node.data.prompt_anchor }),
      ...(nextStepId && { next_step: nextStepId }),
    };
  });
  
  // Return entry_node_id (step_id of first node) and steps
  const entryNodeStepId = steps.length > 0 ? steps[0].step_id : null;
  return {
    entry_node_id: entryNodeStepId,
    steps: steps,
  };
}

/**
 * Build pipeline steps from nodes and edges for backend
 * Used when saving a pipeline
 * @param nodes - All nodes in the pipeline
 * @param edges - All edges in the pipeline
 * @returns Array of pipeline steps with order calculated
 */
export function buildPipelineSteps(
  nodes: Node[],
  edges: Edge[]
): Partial<PipelineStep>[] {
  const orderMap = calculateExecutionOrder(nodes, edges);
  const dataEdges = edges.filter((e) => e.sourceHandle !== 'next_node' && e.sourceHandle !== 'sub_agent_output');

  // Filter to only main pipeline nodes (not sub-agent nodes)
  // SWRM-128: Input IS included as a step (InputProcessor)
  const mainPipelineNodes = nodes.filter((n) => {
    if (n.data.orchestratorNodeId) return false; // Sub-agent nodes handled separately
    return true;
  });

  return mainPipelineNodes.map((node) => {
    // Build input_artifact_keys from data edges targeting this node
    // Preserve type information from node.data.input_artifact_keys if available
    const inputKeys: Record<string, { from_step: string; key: string; source?: InputSource; type?: string }> = {};

    const incomingDataEdges = dataEdges.filter((e) => e.target === node.id);
    for (const edge of incomingDataEdges) {
      if (edge.targetHandle && edge.sourceHandle) {
        // Strip 'input-' and 'output-' prefixes from handle IDs
        const inputKey = edge.targetHandle.replace(/^input-/, '');
        const outputKey = edge.sourceHandle.replace(/^output-/, '');
        
        // Get existing input config to preserve type information
        const existingInput = node.data.input_artifact_keys?.[inputKey];
        
        inputKeys[inputKey] = {
          source: 'artifact' as InputSource, // Default source
          from_step: edge.source,
          key: outputKey,
          ...(existingInput?.type && { type: existingInput.type }), // Preserve type if available
        };
      }
    }

    // If this is an orchestrator, build sub_agents config
    let config = node.data.config || {};
    if (node.type === 'orchestrator') {
      const existingSubAgents = (config.sub_agents || []) as any[];
      
      // Build sub_agents array with chain steps and entry_node_id
      const updatedSubAgents = existingSubAgents.map((subAgent: any) => {
        const chainData = buildSubAgentChainSteps(node.id, subAgent.name, nodes, edges);
        
        return {
          name: subAgent.name,
          display_name: subAgent.display_name,
          description: subAgent.description,
          entry_node_id: chainData.entry_node_id,
          steps: chainData.steps,
        };
      });
      
      config = {
        ...config,
        sub_agents: updatedSubAgents,
      };
    }

    // Include custom_inputs in config (for processors with dynamic inputs)
    if (node.data.custom_inputs && node.data.custom_inputs.length > 0) {
      config = {
        ...config,
        custom_inputs: node.data.custom_inputs,
      };
    }

    // Include dynamic_outputs in config (from <<variable>> syntax in prompts)
    if (node.data.dynamic_outputs && node.data.dynamic_outputs.length > 0) {
      config = {
        ...config,
        dynamic_outputs: node.data.dynamic_outputs,
      };
    }

    return {
      step_id: node.data.step_id,
      order: orderMap.get(node.id) ?? -1, // Use ?? instead of || to allow order 0
      name: node.data.name,
      processor_name: node.data.processor_name,
      input_artifact_keys: inputKeys,
      config,
      on_fail: node.data.on_fail || 'PAUSE',
      ui_position: { x: Math.round(node.position.x), y: Math.round(node.position.y) },
      ...(node.data.prompt_anchor && { prompt_anchor: node.data.prompt_anchor }),
    };
  });
}

/**
 * Find data edges that depend on a specific node
 * Used when removing execution flow to find affected data connections
 * @param nodeId - The node to check dependencies for
 * @param edges - All edges in the pipeline
 * @returns Array of data edges that have this node as source
 */
export function findDependentDataEdges(nodeId: string, edges: Edge[]): Edge[] {
  return edges.filter(
    (e) => e.source === nodeId && e.sourceHandle !== 'next_node'
  );
}

/**
 * Check if a node is connected to the execution flow
 * 
 * Sub-agent nodes are considered "in flow" if they have an orchestratorNodeId,
 * meaning they're part of a sub-agent chain.
 * 
 * @param nodeId - Node to check
 * @param edges - All edges in the pipeline
 * @param nodes - All nodes in the pipeline (needed to check sub-agent membership)
 * @returns true if node has incoming or outgoing execution edge, or is part of a sub-agent
 */
export function isNodeInExecutionFlow(nodeId: string, edges: Edge[], nodes: Node[]): boolean {
  if (nodeId === 'input') return true; // Input is always in flow

  // Check if it's a sub-agent node (part of a sub-agent chain)
  const node = nodes.find((n) => n.id === nodeId);
  if (node?.data?.orchestratorNodeId) {
    return true; // Sub-agent nodes are always "in flow" once connected
  }

  // Regular execution flow check
  const executionEdges = edges.filter((e) => e.sourceHandle === 'next_node');

  return executionEdges.some(
    (e) => e.source === nodeId || e.target === nodeId
  );
}

/**
 * Validate a connection to a sub-agent processor input.
 * 
 * Sub-agent processors have special wiring rules:
 * - They can wire to processors in the same sub-agent chain
 * - They can wire to main pipeline processors that run BEFORE their orchestrator
 * - First processor in sub-agent can wire to orchestrator's LLM Output connector
 * - They CANNOT wire to other sub-agents (execution not guaranteed)
 * - They CANNOT wire to future processors (haven't run yet)
 * 
 * @param sourceNodeId - Source node ID
 * @param sourceHandle - Source handle ID
 * @param targetNodeId - Target node ID
 * @param targetHandle - Target handle ID
 * @param nodes - All nodes in the pipeline
 * @returns Validation result with valid flag and optional reason
 */
export function validateSubAgentConnection(
  sourceNodeId: string,
  sourceHandle: string,
  targetNodeId: string,
  targetHandle: string,
  nodes: Node[]
): { valid: boolean; reason?: string } {
  const targetNode = nodes.find((n) => n.id === targetNodeId);
  const sourceNode = nodes.find((n) => n.id === sourceNodeId);

  // Not a sub-agent target? Normal rules apply
  if (!targetNode?.data?.orchestratorNodeId) {
    return { valid: true };
  }

  // Target IS a sub-agent processor
  const targetOrchestratorId = targetNode.data.orchestratorNodeId;
  const targetSubAgentName = targetNode.data.subAgentName;
  const orchestratorNode = nodes.find((n) => n.id === targetOrchestratorId);

  // Special case: LLM Output connector
  if (sourceHandle === 'llm_output') {
    // Source must be an orchestrator
    if (!sourceNode || sourceNode.data?.processor_name !== 'orchestrator') {
      return {
        valid: false,
        reason: 'LLM Output can only come from an orchestrator'
      };
    }
    
    // Only first processor in sub-agent can connect to LLM Output
    // Find if there are any processors in the same sub-agent with lower order
    const hasEarlierProcessor = nodes.some((n) =>
      n.data?.orchestratorNodeId === targetOrchestratorId &&
      n.data?.subAgentName === targetSubAgentName &&
      n.id !== targetNodeId &&
      (n.data?.order ?? Infinity) < (targetNode.data?.order ?? Infinity)
    );

    if (hasEarlierProcessor) {
      return {
        valid: false,
        reason: 'Only the first processor in a sub-agent can connect to LLM Output'
      };
    }
    return { valid: true };
  }

  // Rule 1: Within same sub-agent chain (allowed)
  if (
    sourceNode?.data?.orchestratorNodeId === targetOrchestratorId &&
    sourceNode?.data?.subAgentName === targetSubAgentName
  ) {
    return { valid: true };
  }

  // Rule 2: From main pipeline BEFORE orchestrator (allowed)
  if (!sourceNode?.data?.orchestratorNodeId) {
    // Source is in main pipeline
    const sourceOrder = sourceNode?.data?.order ?? -1;
    const orchestratorOrder = orchestratorNode?.data?.order ?? Infinity;

    if (sourceOrder < orchestratorOrder && sourceOrder >= 0) {
      return { valid: true };
    }

    return {
      valid: false,
      reason: 'Can only connect from processors that run before this orchestrator'
    };
  }

  // Rule 3: Allow cross-sub-agent connections
  // The source sub-agent must belong to the same orchestrator
  // This creates a dependency: source sub-agent must complete before target sub-agent runs
  if (sourceNode?.data?.orchestratorNodeId === targetOrchestratorId) {
    // Both sub-agents belong to the same orchestrator - allowed
    // The orchestrator will handle sequential execution and artifact resolution
    return { valid: true };
  }

  // Rule 4: Block from completely different orchestrators (not allowed)
  return {
    valid: false,
    reason: 'Cannot connect from sub-agents of different orchestrators'
  };
}

